/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap } from 'rxjs/operators';
import { appModuleActions } from 'src/app/views/users-management/store/action-types';
import { isSaasAcceptedSelector } from 'src/app/store/app.selectors';
import { UtilService } from '@services/util.service';
import { AppHttpService } from '@services/app-http.service';
import { FacSnackbarOptions, SnackbarService, UserInfo } from '@foglight/angular-common';

@Injectable({
  providedIn: 'root',
})
export class SaasAcceptanceGuard implements CanActivate {
  isCloudUser = false;

  constructor(
    private router: Router,
    private store: Store,
    private httpService: AppHttpService,
    private snackbarService: SnackbarService,
    private util: UtilService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.store.select(isSaasAcceptedSelector).pipe(
      switchMap((saasAccepted) => {
        if (saasAccepted === null) {
          return this.verifyWithAPICall(route);
        } else {
          if (saasAccepted) {
            return of(true);
          } else {
            this.router.navigateByUrl('/home');
          }
        }
      })
    );
  }

  verifyWithAPICall(route: ActivatedRouteSnapshot) {
    return this.httpService.getProductInfo().pipe(
      switchMap((productInfo) => {
        let isCloud = productInfo.isCloud;

        if (isCloud) {
          return this.httpService.getUserInfo().pipe(
            switchMap((user: UserInfo) => {
              if (this.util.isCloudUser(user)) {
                return this.httpService.getSaasAddendum().pipe(
                  catchError((error) => {
                    if (error.error.status == 404) {
                      return of(error.error);
                    } else {
                      const options: FacSnackbarOptions = {
                        horizontalPosition: 'end',
                        verticalPosition: 'top',
                        duration: 3000,
                        data: {
                          type: 'error',
                          text: 'Something went wrong. Please try again later.',
                        },
                      };

                      this.snackbarService.show(options);

                      return throwError(() => error);
                    }
                  }),
                  map((user) => {
                    if (user.userName) {
                      this.store.dispatch(
                        appModuleActions.setSaasAddendumAcceptance({
                          saasAccepted: true,
                        })
                      );

                      return true;
                    } else if (user.status == 404) {
                      this.store.dispatch(
                        appModuleActions.setSaasAddendumAcceptance({
                          saasAccepted: false,
                        })
                      );
                      this.router.navigateByUrl('/home');
                    }
                  })
                );
              } else {
                return of(true);
              }
            })
          );
        } else {
          return of(true);
        }
      })
    );
  }
}
